import {
  BufferGeometry,
  Camera,
  ColorRepresentation,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';

import { defaultTheme } from '../../react/themes/default/styled-component/theme';
import {
  Conic,
  getConicDerivative,
  getConicPoint,
} from '../elements/design/design-node/utils/conic';
import { renderOrders } from '../render-orders';
import { getWorldPerPixel } from './utils/world-per-pixel';

const arrowLengthPx = 16;
const arrowWidthPx = 14;
const plusSizePx = 9;
const plusThicknessPx = 2.5;
const outlinePx = 1.5;

/**
 * Gets the corners of the arrow, which points along the x-axis and is centered on the origin.
 * The edges are moved out by the offset, so that an outline has the same width all around.
 */
export const getArrowPoints = (offset = 0) => {
  const points = [
    new Vector3(arrowLengthPx / 2, 0, 0),
    new Vector3(-arrowLengthPx / 2, arrowWidthPx / 2, 0),
    new Vector3(-arrowLengthPx / 2, -arrowWidthPx / 2, 0),
  ];

  // Moving all edges of a triangle out by the same distance is the same as scaling it
  // from the center of its inscribed circle.
  const back = arrowWidthPx;
  const side = points[0].distanceTo(points[1]);
  const incenter = points[0]
    .clone()
    .multiplyScalar(back)
    .addScaledVector(points[1], side)
    .addScaledVector(points[2], side)
    .divideScalar(back + 2 * side);
  const inradius = (arrowLengthPx * arrowWidthPx) / (back + 2 * side);
  const scale = (inradius + offset) / inradius;

  return points.map((point) => point.sub(incenter).multiplyScalar(scale).add(incenter));
};

const outlinePoints = getArrowPoints(outlinePx);
/** The radius of the circle that the arrow turns within. */
export const arrowRadiusPx = Math.max(...outlinePoints.map((point) => point.length()));

const fillColor = defaultTheme.color.brand.dark;
const outlineColor = 0x000000;

const tmpSize = new Vector2();
const tmpLower = new Vector3();
const tmpUpper = new Vector3();

const createMesh = (points: Vector3[], color: ColorRepresentation, renderOrder: number) => {
  const mesh = new Mesh(
    new BufferGeometry().setFromPoints(points),
    new MeshBasicMaterial({
      color,
      side: DoubleSide,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    }),
  );

  mesh.renderOrder = renderOrder;
  mesh.frustumCulled = false;

  return mesh;
};

/**
 * Creates a plus sign centered on the origin.
 */
const createPlus = (
  size: number,
  thickness: number,
  color: ColorRepresentation,
  renderOrder: number,
) => {
  const rectangle = (width: number, height: number) => {
    const [x, y] = [width / 2, height / 2];
    return [
      new Vector3(-x, -y, 0),
      new Vector3(x, -y, 0),
      new Vector3(x, y, 0),
      new Vector3(-x, -y, 0),
      new Vector3(x, y, 0),
      new Vector3(-x, y, 0),
    ];
  };

  return createMesh(
    [...rectangle(size, thickness), ...rectangle(thickness, size)],
    color,
    renderOrder,
  );
};

/**
 * Gets a straight piece of the line centered at t (0 to 1), which has the direction and the slope of
 * the line there. For a straight line it is the whole line moved along itself, for a curve it is a
 * short piece along the curve.
 */
const getLinePiece = ({ start, end, curve }: InclineLine, t: number): [Vector3, Vector3] => {
  if (!curve) {
    const shift = end
      .clone()
      .sub(start)
      .multiplyScalar(t - 0.5);
    return [start.clone().add(shift), end.clone().add(shift)];
  }

  // The derivative has the direction and the slope of the curve.
  const point = getConicPoint(curve, t);
  const step = getConicDerivative(curve, t).multiplyScalar(0.01);

  return [point.clone().sub(step), point.clone().add(step)];
};

const getLinePoint = ({ start, end, curve }: InclineLine, t: number) =>
  curve ? getConicPoint(curve, t) : start.clone().lerp(end, t);

type InclineLine = { start: Vector3; end: Vector3; curve?: Conic };

/**
 * Gaps (pixels) between the arrow and what it is kept clear from.
 */
const clearanceGapPx = 4;

/**
 * Shows the direction of a sloped line with an arrow in the middle of it that points uphill,
 * with a plus sign in its upper left corner. The arrow keeps the same size on screen.
 * On a curve the arrow is on the curve and follows its direction there.
 *
 * The middle of the line can be kept clear, e.g. for the curve handle. Then the arrow is moved
 * uphill along the line, and hidden if the line is too short on screen.
 */
export class PickingIncline extends Group {
  /** The line that is shown, if any. */
  incline?: { start: Vector3; end: Vector3 };

  private line?: InclineLine;
  /** The radius (pixels) around the middle of the line that the arrow is kept outside of. */
  private clearancePx = 0;

  /** Faces the camera in the middle of the line, its children are in pixels. */
  private badge = new Group();
  /** Rotated so that it points uphill along the line on screen. */
  private arrow = new Group();
  /**
   * In the upper left corner of the arrow on screen, outside the circle that the arrow turns within.
   * It is not rotated, since a rotated plus looks like a cross.
   */
  private plus = createPlus(
    plusSizePx,
    plusThicknessPx,
    0x000000,
    renderOrders.pickers.inclineGuide,
  );

  constructor() {
    super();

    this.visible = false;

    const meshes = [
      createMesh(outlinePoints, outlineColor, renderOrders.pickers.inclineGuideOutline),
      createMesh(getArrowPoints(), fillColor, renderOrders.pickers.inclineGuide),
      this.plus,
    ];

    // All of them update the badge, since any of them can be rendered first.
    for (const mesh of meshes) {
      mesh.onBeforeRender = (renderer, scene, camera) => this.updateBadge(renderer, camera);
    }

    const plusOffset = arrowRadiusPx + 1;
    this.plus.position.set(-plusOffset, plusOffset, 0);

    this.arrow.add(meshes[0], meshes[1]);
    this.badge.add(this.arrow, this.plus);
    this.add(this.badge);
  }

  /**
   * @param curve Shows the arrow along the curve from start to end.
   * @param clearancePx Keeps the arrow outside of this radius (pixels) around the middle of the line.
   */
  show(start: Vector3, end: Vector3, curve?: Conic, clearancePx = 0) {
    this.incline = { start: start.clone(), end: end.clone() };
    this.line = { start: start.clone(), end: end.clone(), curve };
    this.clearancePx = clearancePx;
    this.visible = true;
  }

  hide() {
    this.incline = undefined;
    this.line = undefined;
    this.visible = false;
  }

  /**
   * Gets where (t, 0 to 1) along the line the arrow is placed. It is in the middle, unless the middle
   * is kept clear, then it is moved uphill until it is outside of the clearance on screen.
   * Returns undefined if the arrow does not fit on the line.
   */
  private getArrowParameter(line: InclineLine, camera: Camera): number | undefined {
    if (this.clearancePx <= 0) {
      return 0.5;
    }

    const distancePx = arrowRadiusPx + this.clearancePx + clearanceGapPx;
    const uphillEnd = line.end.z >= line.start.z ? 1 : 0;

    const toScreen = (t: number) => {
      const point = getLinePoint(line, t).project(camera);
      return new Vector2((point.x * tmpSize.x) / 2, (point.y * tmpSize.y) / 2);
    };

    const middle = toScreen(0.5);
    const distanceTo = (t: number) => toScreen(t).distanceTo(middle);

    // The whole arrow has to fit on the line.
    if (distanceTo(uphillEnd) < distancePx + arrowRadiusPx) {
      return undefined;
    }

    // The distance on screen grows towards the end, since a curve sweeps less than a full circle.
    let [near, far] = [0.5, uphillEnd];

    for (let i = 0; i < 20; i++) {
      const t = (near + far) / 2;
      [near, far] = distanceTo(t) < distancePx ? [t, far] : [near, t];
    }

    return (near + far) / 2;
  }

  /**
   * Places the badge on the line and points the arrow uphill along the line on screen.
   */
  private updateBadge(renderer: WebGLRenderer, camera: Camera) {
    if (!this.line) {
      return;
    }

    renderer.getSize(tmpSize);

    const t = this.getArrowParameter(this.line, camera);

    if (t === undefined) {
      // Hidden right away, since the badge is already being rendered.
      this.badge.scale.setScalar(0);
      this.badge.updateMatrixWorld(true);
      return;
    }

    const [start, end] = getLinePiece(this.line, t);
    const [lower, upper] = start.z <= end.z ? [start, end] : [end, start];
    tmpLower.copy(lower).project(camera);
    tmpUpper.copy(upper).project(camera);

    const angle = Math.atan2(
      (tmpUpper.y - tmpLower.y) * tmpSize.y,
      (tmpUpper.x - tmpLower.x) * tmpSize.x,
    );

    this.badge.position.addVectors(lower, upper).multiplyScalar(0.5);
    camera.getWorldQuaternion(this.badge.quaternion);
    this.badge.scale.setScalar(getWorldPerPixel(renderer, camera, this.badge.position));
    this.arrow.rotation.z = angle;
    this.badge.updateMatrixWorld(true);
  }
}
